import React from 'react'
import {
  IQuizOption,
  IQuizQuestion,
  IQuizResource,
  IWorkplaceQuizModuleState,
  IWorkplaceQuizState,
} from '../../../models/WorkplaceQuiz'

const { createContext, useState } = React

interface IWorkplaceQuizContext {
  currentState: IWorkplaceQuizModuleState
  updateAnswer: (
    quizId: number,
    question: IQuizQuestion,
    correct: boolean,
    options: IQuizOption[],
    failedResources: IQuizResource[]
  ) => void
  getLastQuestion: (quizId: number) => string
  getQuizState: (quizId: number) => IWorkplaceQuizState
  quizCompleted: (quizId: number) => void
  allQuizCompleted: () => boolean
}

type Props = {
  currentState: IWorkplaceQuizModuleState
  children: React.ReactNode
}

export const WorkplaceQuizContext = createContext<IWorkplaceQuizContext>({
  currentState: null,
  updateAnswer: async () => {
    // do nothing
  },
  getLastQuestion: () => {
    // do nothing
    return null
  },
  getQuizState: () => {
    // do nothing
    return null
  },
  quizCompleted: async () => {
    // do nothing
  },
  allQuizCompleted: () => {
    // do nothing
    return false
  },
})

export const WorkplaceQuizDetailProvider = (props: Props) => {
  const getLastQuestion = (quizId: number) => {
    const answers = state.currentState.answers.filter(
      (p) => p.quizId === quizId
    )
    if (answers === null || answers === undefined || answers.length <= 0) {
      return null
    }
    const lastAnswer = answers.pop()
    if (lastAnswer === null || lastAnswer === undefined) {
      return null
    }
    return lastAnswer.questionId
  }

  const getQuizState = (quizId: number) => {
    const current = state.currentState
    if (current) {
      const index = current.quizStatus.findIndex((q) => q.quizId === quizId)
      if (index >= 0) {
        return current.quizStatus[index]
      }
    }
    return null
  }

  const allQuizCompleted = () => {
    const current = state.currentState
    if (current) {
      const completedQuiz: IWorkplaceQuizState[] = current.quizStatus.filter(
        (q) => q.completed === true
      )
      if (
        completedQuiz.length >= 0 &&
        completedQuiz.length === current.quizStatus.length
      ) {
        return true
      }
    }
    return false
  }

  const quizCompleted = (quizId: number) => {
    const current = state.currentState
    if (current) {
      const index = current.quizStatus.findIndex((q) => q.quizId === quizId)
      if (index >= 0) {
        const quizStatus: IWorkplaceQuizState = current.quizStatus[index]
        if (quizStatus) {
          quizStatus.completed = true
          updateStatus(quizStatus)
        }
      }
    }
    return null
  }

  const updateStatus = (quizStatus: IWorkplaceQuizState) => {
    const current = state.currentState
    const quizStatuses: IWorkplaceQuizState[] = current.quizStatus.filter(
      (p) => p.id !== quizStatus.quizId
    )
    current.quizStatus = quizStatuses
  }

  const updateAnswer = async (
    quizId: number,
    question: IQuizQuestion,
    correct: boolean,
    options: IQuizOption[],
    failedResources: IQuizResource[]
  ) => {
    const current: IWorkplaceQuizModuleState = state.currentState
    //Replace the quiz status with the new status and update the correct count
    const index = current.quizStatus.findIndex((q) => q.quizId === quizId)
    if (correct) {
      //Add the correct answer to the bucket
      const newCorrectAnswered = current.quizStatus[
        index
      ].correctAnswered.filter((p) => p !== question.textId)
      newCorrectAnswered.push(question.textId)
      current.quizStatus[index].correctAnswered = newCorrectAnswered

      //Remove the answer that is right now from the bucket.
      const newWrongAnswered = current.quizStatus[index].wrongAnswered.filter(
        (p) => p !== question.textId
      )
      current.quizStatus[index].wrongAnswered = newWrongAnswered
    } else {
      //Add the wrong answer to the bucket
      const newWrongAnswered = current.quizStatus[index].wrongAnswered.filter(
        (p) => p !== question.textId
      )
      newWrongAnswered.push(question.textId)
      current.quizStatus[index].wrongAnswered = newWrongAnswered

      //Remove the answer that is right now from the bucket.
      const newCorrectAnswered = current.quizStatus[
        index
      ].correctAnswered.filter((p) => p !== question.textId)
      current.quizStatus[index].correctAnswered = newCorrectAnswered
    }

    //Replace the question answers with the new answers
    const newAnswers = state.currentState.answers.filter(
      (p) => p.questionId !== question.textId
    )
    for (let index = 0; index < options.length; index++) {
      newAnswers.push(options[index])
    }

    //Replace and add the failed resource
    const newFailedResources = current.quizStatus[index].failedResources.filter(
      (p) => p.questionId !== question.textId
    )

    if (!correct && failedResources) {
      const existingFailedResources = current.quizStatus[index].failedResources
      for (let index = 0; index < failedResources.length; index++) {
        const resource = failedResources[index]

        //Check if exists in remainder resources.
        const exists = current.quizStatus[index].remainderResources.filter(
          (p) => p.id === resource.id
        )

        //Check if exists in failed resources.
        const existsFailed = existingFailedResources.filter(
          (p) => p.resource !== null && p.resource.id === resource.id
        )

        if (
          (exists === null || exists.length <= 0) &&
          (existsFailed === null || existsFailed.length <= 0)
        ) {
          newFailedResources.push({
            questionId: question.textId,
            resource: failedResources[index],
          })
        }
      }
    }

    current.quizStatus[index].failedResources = newFailedResources
    current.answers = newAnswers
    update((prev) => ({
      ...prev,
      currentState: current,
    }))
  }

  const [state, update] = useState<IWorkplaceQuizContext>({
    currentState: props.currentState,
    getLastQuestion: getLastQuestion,
    updateAnswer: updateAnswer,
    getQuizState: getQuizState,
    quizCompleted: quizCompleted,
    allQuizCompleted: allQuizCompleted,
  })

  return (
    <WorkplaceQuizContext.Provider value={state}>
      {props.children}
    </WorkplaceQuizContext.Provider>
  )
}

export default WorkplaceQuizContext
